import React, { useState, useEffect, useRef } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { FaBars, FaTimes, FaCaretDown, FaCaretUp } from "react-icons/fa";
import { HiOutlineShoppingBag } from "react-icons/hi";
import { useCart } from "../../context/CartContext";
import { usePreloader } from "../../context/PreloaderContext"; //
import Notification from "./Notification";
import logoWhite from "../../assets/logo.png";
import logoBlack from "../../assets/blacklogo.png";
import styles from "./Navbar.module.css";

const lightHeaderPaths = ["/checkout"];

function Navbar() {
  const [click, setClick] = useState(false);
  const [dropdown, setDropdown] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  const { triggerTransition } = usePreloader(); //
  const { cartItems, notification, clearNotification } = useCart();

  const totalItemsInCart = cartItems.reduce(
    (total, item) => total + item.quantity,
    0
  );

  const isLightHeaderPage = lightHeaderPaths.includes(location.pathname);
  const isHeaderDark = isScrolled || !isLightHeaderPage;
  const mobileIconColor = click ? "#fff" : isHeaderDark ? "#ccc" : "#444";

  const handleClick = () => setClick(!click);
  const closeAllMenus = () => {
    setClick(false);
    setDropdown(false);
  };

  // Luxury Preloader Click Handler
  const handleNavClick = (e, path) => {
    e.preventDefault(); // Stop instant navigation to allow preloader to cover screen

    if (location.pathname !== path) {
      triggerTransition(path); // Trigger the curtain transition
    }
    closeAllMenus();
  };

  const onMouseEnter = () => {
    if (window.innerWidth >= 960) setDropdown(true);
  };
  const onMouseLeave = () => {
    if (window.innerWidth >= 960) setDropdown(false);
  };

  const toggleDropdown = (e) => {
    if (window.innerWidth < 960) {
      const link = e.target.closest("." + styles.dropdownToggleLink);
      if (link) {
        e.preventDefault();
        setDropdown((prev) => !prev);
      }
    }
  };

  const changeBackground = () => setIsScrolled(window.scrollY >= 80);

  useEffect(() => {
    window.addEventListener("scroll", changeBackground);
    changeBackground();
    return () => window.removeEventListener("scroll", changeBackground);
  }, []);

  useEffect(() => {
    closeAllMenus();
  }, [location]);

  const isCoffeeShopActive = location.pathname.startsWith("/locations");
  const currentLogo = isHeaderDark ? logoWhite : logoBlack;

  const navbarClasses = [
    styles.navbar,
    isHeaderDark ? styles.darkHeader : styles.lightHeader,
    isScrolled ? styles.scrolled : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <nav className={navbarClasses}>
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={clearNotification}
        />
      )}

      <div
        className={`${styles.navOverlay} ${click ? styles.active : ""}`}
        onClick={closeAllMenus}
      ></div>

      <div className={styles.navbarContainer}>
        <Link
          to="/"
          className={styles.navbarLogo}
          onClick={(e) => handleNavClick(e, "/")}
        >
          <img
            src={currentLogo}
            alt="Dukamo Coffee Logo"
            className={styles.logoImage}
          />
        </Link>

        <div className={styles.mobileIconsWrapper}>
          <Link
            to="/cart"
            className={styles.navCartHeaderMobile}
            onClick={closeAllMenus}
            style={{ color: mobileIconColor }}
          >
            <HiOutlineShoppingBag />
            {totalItemsInCart > 0 && (
              <span className={styles.cartBadgeHeader}>{totalItemsInCart}</span>
            )}
          </Link>
          <div
            className={styles.menuIcon}
            onClick={handleClick}
            style={{ color: mobileIconColor }}
          >
            {click ? <FaTimes /> : <FaBars />}
          </div>
        </div>

        <ul
          className={
            click ? `${styles.navMenu} ${styles.active}` : styles.navMenu
          }
        >
          <li className={styles.navItem}>
            <NavLink
              to="/"
              className={({ isActive }) =>
                `${styles.navLink} ${isActive ? styles.active : ""}`
              }
              onClick={(e) => handleNavClick(e, "/")}
              end
            >
              Home
            </NavLink>
          </li>
          <li className={styles.navItem}>
            <NavLink
              to="/about"
              className={({ isActive }) =>
                `${styles.navLink} ${isActive ? styles.active : ""}`
              }
              onClick={(e) => handleNavClick(e, "/about")}
            >
              About Us
            </NavLink>
          </li>

          <li
            ref={dropdownRef}
            className={`${styles.navItem} ${styles.dropdown} ${
              dropdown ? styles.active : ""
            }`}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            <Link
              to="#"
              className={`${styles.navLink} ${styles.dropdownToggleLink} ${
                isCoffeeShopActive ? styles.active : ""
              }`}
              onClick={toggleDropdown}
            >
              Coffee Shops{" "}
              {dropdown ? (
                <FaCaretUp
                  className={`${styles.dropdownIcon} ${styles.open}`}
                />
              ) : (
                <FaCaretDown className={styles.dropdownIcon} />
              )}
            </Link>
            <ul className={styles.dropdownMenu}>
              <li>
                <NavLink
                  to="/locations/mafi"
                  className={styles.dropdownLink}
                  onClick={(e) => handleNavClick(e, "/locations/mafi")}
                >
                  Dukamo Mafi
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/locations/bherawi"
                  className={styles.dropdownLink}
                  onClick={(e) => handleNavClick(e, "/locations/bherawi")}
                >
                  Dukamo Bherawi
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/locations/hawassa"
                  className={styles.dropdownLink}
                  onClick={(e) => handleNavClick(e, "/locations/hawassa")}
                >
                  Dukamo Hawassa
                </NavLink>
              </li>
            </ul>
          </li>

          <li className={styles.navItem}>
            <NavLink
              to="/roastery"
              className={({ isActive }) =>
                `${styles.navLink} ${isActive ? styles.active : ""}`
              }
              onClick={(e) => handleNavClick(e, "/roastery")}
            >
              Roastery
            </NavLink>
          </li>
          <li className={styles.navItem}>
            <NavLink
              to="/menu"
              className={({ isActive }) =>
                `${styles.navLink} ${isActive ? styles.active : ""}`
              }
              onClick={(e) => handleNavClick(e, "/menu")}
            >
              Menu
            </NavLink>
          </li>
          <li className={styles.navItem}>
            <NavLink
              to="/products"
              className={({ isActive }) =>
                `${styles.navLink} ${isActive ? styles.active : ""}`
              }
              onClick={(e) => handleNavClick(e, "/products")}
            >
              Products
            </NavLink>
          </li>
          <li className={styles.navItem}>
            <NavLink
              to="/contact"
              className={({ isActive }) =>
                `${styles.navLink} ${isActive ? styles.active : ""}`
              }
              onClick={(e) => handleNavClick(e, "/contact")}
            >
              Contact Us
            </NavLink>
          </li>

          <li className={`${styles.navItem} ${styles.navCartDesktop}`}>
            <NavLink
              to="/cart"
              className={({ isActive }) =>
                `${styles.navLink} ${styles.cartIconWrapper} ${
                  isActive ? styles.active : ""
                }`
              }
              onClick={closeAllMenus}
            >
              <HiOutlineShoppingBag className={styles.cartIcon} />
              {totalItemsInCart > 0 && (
                <span className={styles.cartBadge}>{totalItemsInCart}</span>
              )}
            </NavLink>
          </li>

          <li className={`${styles.navItem} ${styles.navCartMobile}`}>
            <NavLink
              to="/cart"
              className={({ isActive }) =>
                `${styles.navLink} ${styles.cartLinkMobile} ${
                  isActive ? styles.active : ""
                }`
              }
              onClick={closeAllMenus}
            >
              <HiOutlineShoppingBag /> Cart{" "}
              {totalItemsInCart > 0 && (
                <span className={`${styles.cartBadge} ${styles.mobile}`}>
                  {" "}
                  {totalItemsInCart}{" "}
                </span>
              )}
            </NavLink>
          </li>
        </ul>
      </div>
    </nav>
  );
}

export default Navbar;
